import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/forms/PasswordInput';
import API from '@/lib/api';
import { passwordMinLength } from '@/config/passwordMinLength';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface ChangePasswordFormProps {
  action: string;
  onSuccess?: (response: unknown) => void;
}

interface ChangePasswordFormValues {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

export const ChangePasswordForm = ({
  action,
  onSuccess,
}: ChangePasswordFormProps) => {
  const { t } = useTranslation();

  const schema = yup
    .object({
      oldPassword: yup.string().required(t('user.settings.changePassword.form.oldPassword.errors.required')),
      newPassword: yup
        .string()
        .required(t('user.settings.changePassword.form.newPassword.errors.required'))
        .min(
          passwordMinLength,
          t('user.settings.changePassword.form.newPassword.errors.minLength', { minLength: passwordMinLength })
        )
        .notOneOf(
          [yup.ref('oldPassword')],
          t('user.settings.changePassword.form.newPassword.errors.notSame')
        ),
      newPasswordConfirmation: yup
        .string()
        .required(t('user.settings.changePassword.form.newPasswordConfirmation.errors.required'))
        .oneOf([yup.ref('newPassword')], t('user.settings.changePassword.form.newPasswordConfirmation.errors.notSame')),
    })
    .required();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setError,
    watch,
  } = useForm<ChangePasswordFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const onSubmit = (values: ChangePasswordFormValues) => {
    if (isSubmitting) return;
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);

    API.patch(action, {
      oldPassword: values.oldPassword,
      newPassword: values.newPassword,
    })
      .then((response) => {
        reset();
        onSuccess?.(response);
      })
      .catch((error) => {
        console.error(error);

        if (error.response.status === 400) {
          // toast.error(t('user.settings.changePassword.submit.errors.oldPasswordIncorrect'), {
          //   duration: 5000,
          //   closeButton: true,
          // });
          setError('oldPassword', {
            message: t('user.settings.changePassword.submit.errors.oldPasswordIncorrect'),
          });
        }

        return error;
      }).finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <form
      method="POST"
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6 min-w-[300px]"
    >
      <div className="space-y-2">
        <Label htmlFor="oldPassword">{t('user.settings.changePassword.form.oldPassword.label')}</Label>
        <PasswordInput
          id="oldPassword"
          placeholder={t('user.settings.changePassword.form.oldPassword.label')}
          disabled={isSubmitting}
          autoComplete="current-password"
          {...register('oldPassword', {
            required: t('user.settings.changePassword.form.oldPassword.errors.required'),
          })}
        />
        {errors.oldPassword && (
          <p className="text-sm text-destructive">
            {errors.oldPassword.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="newPassword">{t('user.settings.changePassword.form.newPassword.label')}</Label>
        <PasswordInput
          id="newPassword"
          placeholder={t('user.settings.changePassword.form.newPassword.label')}
          disabled={isSubmitting}
          autoComplete="new-password"
          minLength={passwordMinLength}
          {...register('newPassword', {
            required: t('user.settings.changePassword.form.newPassword.errors.required'),
          })}
        />
        {errors.newPassword && (
          <p className="text-sm text-destructive">
            {errors.newPassword.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="newPasswordConfirmation">{t('user.settings.changePassword.form.newPasswordConfirmation.label')}</Label>
        <PasswordInput
          id="newPasswordConfirmation"
          placeholder={t('user.settings.changePassword.form.newPasswordConfirmation.label')}
          disabled={isSubmitting}
          autoComplete="new-password"
          {...register('newPasswordConfirmation', {
            required: t('user.settings.changePassword.form.newPasswordConfirmation.errors.required'),
            validate: (value) =>
              value === watch('newPassword') || t('user.settings.changePassword.form.newPasswordConfirmation.errors.notSame'),
          })}
        />
        {errors.newPasswordConfirmation && (
          <p className="text-sm text-destructive">
            {errors.newPasswordConfirmation.message}
          </p>
        )}
      </div>

      <Button className="w-full" disabled={isSubmitting} type="submit">
        {isSubmitting ? t('user.settings.changePassword.submit.loading') : t('user.settings.changePassword.submit.label')}
      </Button>
    </form>
  );
};
